import { Injectable, type Provider } from '@angular/core';

import { ok, type ApiResult } from '../../../core/models/api-result';
import { previewHubCardPost, previewHubCardPosts } from './hub-card-preview.service';
import { HubFeedService } from './hub-feed.service';
import type { HubFeedItem, HubFeedPage, HubFeedQuery, HubFeedReadState } from './hub-feed.models';
import {
  HUBS_READ_PREVIEW_NOW,
  HUBS_READ_PREVIEW_REPLIES,
  HUBS_READ_PREVIEW_SEEN,
  hubsReadPreviewHook,
  hubsReadPreviewSince,
} from './hubs-read-preview';

/** The server counts "new" back at most 14 days, however old `since` is. */
const NEW_WINDOW_MS = 14 * 24 * 3_600_000;

function publishedMs(item: HubFeedItem): number {
  const t = item.publishedAt ? Date.parse(item.publishedAt) : NaN;
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
}

function byPublishedDesc(a: HubFeedItem, b: HubFeedItem): number {
  const d = publishedMs(b) - publishedMs(a);
  if (d) return d;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/** The API's Hot: (reactions + 2 x comments + 0.1 x views) / (hours + 2)^1.5. */
export function previewHotScore(item: HubFeedItem, now: number): number {
  const points =
    (item.reactions?.count ?? 0) + 2 * (item.commentCount ?? 0) + 0.1 * (item.viewCount ?? 0);
  if (points <= 0) return 0;
  const hours = Math.max(0, (now - publishedMs(item)) / 3_600_000);
  return points / Math.pow(hours + 2, 1.5);
}

/**
 * THE WALL, WITHOUT A SESSION.
 *
 * `/preview/hubs` is guard-free so the surface can be reviewed and
 * screenshotted without logging in — but `contentFeed` is viewer-scoped, so
 * unauthenticated it 401s and the wall is an error banner. This serves the
 * prototype's own corpus instead.
 *
 * ── IT OVERRIDES ONE METHOD, DELIBERATELY ─────────────────────────────────
 * Only `fetch`. Paging, the cursor, the seed, the append-versus-replace split
 * and every signal are the REAL service's, inherited unchanged — so what the
 * harness exercises is the code that ships. A preview that reimplemented the
 * service would be a second implementation, and a harness that does not run
 * the thing it is reviewing is worth very little: it goes green while the real
 * path is broken, which is the exact failure preview harnesses are supposed to
 * prevent.
 *
 * The lenses are applied here because the SERVER applies them in the real
 * path, and a harness that ignored them would show a filtered wall as
 * unfiltered — the reviewer would be looking at the wrong answer and could not
 * tell.
 */
/**
 * A Spotlight from the hub's Spotlights tab (`SpotlightPlacesHubPreviewData`),
 * as the post the hub viewer reads: the tab opens it in the hub's reader
 * (`?read=`), so the harness must be able to resolve its ids. Lazy, as the
 * corpus is.
 */
export async function previewSpotlightPost(id: string): Promise<HubFeedItem | null> {
  const { PLACES_FIXTURE } = await import('../../spotlight/places/spotlight-places-fixture');
  const sp = PLACES_FIXTURE.find(item => item.id === id);
  if (!sp) return null;
  return {
    id: sp.id,
    kind: 'ARTICLE',
    format: null,
    source: 'COMMUNITY',
    title: sp.title,
    body: `<p>${sp.title}.</p>`,
    publishedAt: sp.publishedAt,
    readTimeMinutes: sp.readTimeMinutes,
    pinned: false,
    viewerStarred: false,
    commentCount: 0,
    viewCount: 0,
    shareCount: 0,
    topics: [],
    hub: sp.hubId
      ? { _id: sp.hubId, name: sp.hubName ?? '', type: 'CUSTOM', primaryColor: null, memberCount: null, memberRole: null, logo: null, canManage: false }
      : null,
    author: null,
    media: null,
    reactions: { count: 0, viewerHasReacted: false, viewerReaction: null },
    poll: null,
    questionId: null,
    viewerVotedOptionIds: [],
    spotlight: sp.mainAction
      ? { needsYou: sp.needsYou, mainAction: { widgetId: sp.mainAction.widgetId, kind: sp.mainAction.kind } }
      : null,
  };
}

@Injectable()
export class HubFeedPreviewService extends HubFeedService {
  /** The window hook the read-state journey drives (`hubs-read-preview.ts`). */
  private readonly hook = hubsReadPreviewHook();

  /**
   * The corpus is pinned to 21 Aug 2026, so "now" for read state is too: a
   * day later, so Friday's posts are yesterday's and the 14-day end of the
   * stream falls inside the corpus.
   */
  override clockMs(): number {
    return Date.parse(HUBS_READ_PREVIEW_NOW);
  }

  /** The pill's poll: whatever `window.__hubsPreview.arrive(n)` has added. */
  protected override async fetchNewCount(): Promise<number | null> {
    return this.hook.arrivals;
  }

  /**
   * An author's edit, in the harness: the server's answer is the words that
   * were sent, so the real `edit` path patches both stores as it would.
   */
  protected override async sendEdit(
    id: string,
    input: Record<string, string>,
  ): Promise<ApiResult<{ _id: string; title: string | null; body: string | null } | null>> {
    return ok({ _id: id, title: input['title'] ?? null, body: input['body'] ?? null });
  }

  /** An author's delete, in the harness: the server says yes. */
  protected override async sendDelete(_id: string): Promise<ApiResult<boolean | null>> {
    return ok(true);
  }

  /**
   * One post by id, from the corpus. A pinned story is left out of the
   * stream (`excludeIds`), so opening it from the Home top deep-fetches it
   * (`resolveById`), as a shared link would; without this the harness asked
   * the real API and the reader stayed shut.
   */
  protected override async fetchOne(id: string): Promise<HubFeedItem | null> {
    const { HUBS_PREVIEW_ITEMS } = await import('./hubs-preview.fixtures');
    return (
      HUBS_PREVIEW_ITEMS.find(item => item.id === id) ??
      (await previewHubCardPost(id)) ??
      (await previewSpotlightPost(id))
    );
  }

  protected override async fetch(
    query: HubFeedQuery,
  ): Promise<HubFeedPage | null> {
    const first = query.first ?? 24;

// ⚠ LAZY, AND THE WHOLE POINT OF THIS FILE'S EXISTENCE IS WHY.
// `app.routes.ts` imports the preview PROVIDER as a value, so anything this
// module imports at the top level lands in the EAGER `main.js` — for every
// visitor, on every route, whether or not they ever open `/preview/hubs`. The
// fixture corpus is 84 kB of prototype content. Imported here, inside the one
// async method that reads it, the module registry still dedupes it across the
// preview services and nobody who is not previewing pays for any of it.
    const { HUBS_PREVIEW_ITEMS } = await import('./hubs-preview.fixtures');
    let pool = HUBS_PREVIEW_ITEMS;
    if (query.hubId) pool = pool.filter(i => i.hub?._id === query.hubId);
    if (query.authorId) pool = pool.filter(i => i.author?._id === query.authorId);
    if (query.kinds?.length) {
      const kinds = new Set(query.kinds);
      pool = pool.filter(i => kinds.has(i.kind));
    }
    if (query.sources?.length) {
      const sources = new Set(query.sources);
      pool = pool.filter(i => sources.has(i.source));
    }
    // ⚠ THE FORMAT LENS, WITHOUT WHICH THREE OF EIGHT CHIPS DO NOTHING HERE.
    // Video, Photos and Written are answered by `postFormat` rather than by
    // `kinds` — there is no VIDEO in `ContentKind` — so a harness that applied
    // only the kinds left those three chips lighting up over an unchanged
    // wall. A reviewer would have read that as the filter being broken.
    if (query.postFormat) {
      pool = pool.filter(i => i.format === query.postFormat);
    }

    // THE ORDER IS THE SERVER'S, and these are stand-ins for it. Newest and
    // Hot follow the API's own definitions (`feed-sort.ts` in
    // rdx-platform-api) so the harness reads the way dev will; with no sort,
    // a fixed offset by seed stands in for the rotation.
    if (query.excludeIds?.length) {
      const out = new Set(query.excludeIds);
      pool = pool.filter(i => !out.has(i.id));
    }

    if (query.sort === 'NEWEST' && query.readState) {
      // Same-minute posts from one hub sit together, as a busy hub's do on
      // the server, so the harness has a run for the stream to fold.
      pool = [...pool].sort(
        (a, b) =>
          publishedMs(b) - publishedMs(a) ||
          (a.hub?._id ?? '').localeCompare(b.hub?._id ?? '') ||
          byPublishedDesc(a, b),
      );
    } else if (query.sort === 'NEWEST') {
      pool = [...pool].sort(byPublishedDesc);
    } else if (query.sort === 'HOT') {
      const now = Date.now();
      pool = [...pool].sort(
        (a, b) => previewHotScore(b, now) - previewHotScore(a, now) || byPublishedDesc(a, b),
      );
    } else {
      const seed = query.seed ?? 0;
      if (seed > 0 && pool.length > 0) {
        const at = (seed * 7) % pool.length;
        pool = [...pool.slice(at), ...pool.slice(0, at)];
      }
    }

    // `__hubsPreview.publish(n)`: n posts that arrived since the page loaded
    // lead the next whole-feed read, so the pill's reload inserts real rows.
    if (this.hook.fresh > 0 && query.readState && !query.hubId && !query.authorId) {
      const now = this.clockMs();
      const fresh = pool.slice(0, this.hook.fresh).map((item, k) => ({
        ...item,
        id: `${item.id}-fresh`,
        publishedAt: new Date(now - (k + 1) * 60_000).toISOString(),
      }));
      pool = [...fresh, ...pool];
    }

    // "Tell another hub" (sb82.21): hub-card posts, on top. The seeded ones
    // on their hub's page only; shared ones there and in Everything. Never
    // under a narrowing lens, which a plain post would not pass anyway.
    if (!query.authorId && !query.kinds?.length && !query.sources?.length && !query.postFormat) {
      const out = new Set(query.excludeIds ?? []);
      const cards = (await previewHubCardPosts(query.hubId)).filter(c => !out.has(c.id));
      if (cards.length) pool = [...cards, ...pool];
    }

    const start = query.after ? Number(query.after) : 0;
    const raw = pool.slice(start, start + first);
    const seenNow = new Set(this.hook.seen);
    const slice = query.readState ? raw.map(item => withReadState(item, seenNow)) : raw;
    const next = start + slice.length;

    return {
      ...(query.readState ? { readState: this.readStateFor(pool, slice, query) } : {}),
      items: slice,
      totalCount: pool.length,
      // The real cursor is opaque and positional; this one is an index, which
      // is all a fixture needs and is obviously not a real cursor to anyone
      // reading it in a URL.
      endCursor: next < pool.length ? String(next) : null,
      hasNextPage: next < pool.length,
    };
  }

  private readStateFor(
    pool: readonly HubFeedItem[],
    slice: readonly HubFeedItem[],
    query: HubFeedQuery,
  ): HubFeedReadState {
    return previewReadState(pool, slice, query, this.clockMs());
  }
}

/**
 * THE READ STATE A SERVER WOULD HAVE ANSWERED, for the harness. Public on the
 * class for nothing but its own `fetch`; module-level so it stays pure.
 */
export function previewReadState(
  pool: readonly HubFeedItem[],
  slice: readonly HubFeedItem[],
  query: HubFeedQuery,
  nowMs: number,
): HubFeedReadState {
  const since = query.since !== undefined ? query.since : hubsReadPreviewSince();
  if (!since) {
    // A first visit ever: the server answers with no `since` and no count.
    return { since: null, newCount: null, sinceBoundaryCursor: null, boundaryItemId: null };
  }
  const sinceMs = Date.parse(since);
  const floor = Math.max(sinceMs, nowMs - NEW_WINDOW_MS);
  const boundary = slice.find(i => publishedMs(i) <= sinceMs);
  return {
    since,
    // The count belongs to page one, as on the server.
    newCount: query.after ? null : pool.filter(i => publishedMs(i) > floor).length,
    sinceBoundaryCursor: boundary ? `boundary:${boundary.id}` : null,
    boundaryItemId: boundary?.id ?? null,
  };
}

/**
 * One fixture row as the server would return it to this reader. A post the
 * seen beacon reported during this visit comes back seen just after the
 * harness's "now", as it would from the server after a reload.
 */
function withReadState(item: HubFeedItem, seenThisVisit: ReadonlySet<string>): HubFeedItem {
  const replies = HUBS_READ_PREVIEW_REPLIES[item.id];
  const seen = HUBS_READ_PREVIEW_SEEN.has(item.id) || replies !== undefined;
  const seenAt = seenThisVisit.has(item.id)
    ? new Date(Date.parse(HUBS_READ_PREVIEW_NOW) + 60_000).toISOString()
    : seen
      ? '2026-08-21T06:00:00.000Z'
      : null;
  return {
    ...item,
    viewerSeenAt: seenAt,
    viewerOpenedAt: replies !== undefined ? '2026-08-20T18:00:00.000Z' : null,
    newReplyCount: replies ?? (seen ? 0 : null),
  };
}

/** Drop into a route's `providers` to serve the corpus instead of the API. */
export const HUB_FEED_PREVIEW_PROVIDER: Provider = {
  provide: HubFeedService,
  useClass: HubFeedPreviewService,
};
